/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import {cloneElement, type FC, type JSX, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {useImpression} from '~/common/hooks/useImpression';
import {willChildReturnNull} from '~/common/utils';
import {
  selectHostUploadProcessing,
  selectHostUploadProgress,
  selectSharedUploadProcessing,
  selectSharedUploadProgress,
} from '~/ViewInvitation/selectors/photos';

import './Module.sass';

interface ModuleProps {
  children: JSX.Element;
  moduleName: string;
  impressionEvent?: string;
  isPreview?: boolean;
  readonly?: boolean;
  [key: string]: any;
}

export const Module: FC<ModuleProps> = ({
  children,
  moduleName,
  impressionEvent = undefined,
  isCreateEditPreview,
  readonly,
  ...acyclicalProps
}) => {
  const tracking: Record<string, any> = {};
  const trackingRef = useRef(null);
  const hostUploadProgress = useSelector(selectHostUploadProgress);
  const sharedUploadProgress = useSelector(selectSharedUploadProgress);
  const hostUploadProcessing = useSelector(selectHostUploadProcessing);
  const sharedUploadProcessing = useSelector(selectSharedUploadProcessing);

  const uploadProgress = useMemo(() => {
    if (moduleName === 'host_photo') return hostUploadProgress;
    if (moduleName === 'photo') return sharedUploadProgress;
    return undefined;
  }, [moduleName, hostUploadProgress, sharedUploadProgress]);

  const processing = useMemo(() => {
    if (moduleName === 'host_photo') return hostUploadProcessing;
    if (moduleName === 'photo') return sharedUploadProcessing;
    return undefined;
  }, [moduleName, hostUploadProcessing, sharedUploadProcessing]);

  if (impressionEvent) {
    tracking.ref = trackingRef;
    useImpression(trackingRef, impressionEvent);
  }

  const [moduleAnimation] = useAutoAnimate({
    duration: 350,
    easing: 'ease-in-out',
  });

  const showProgress = typeof uploadProgress === 'number';
  const style = {
    width: `${uploadProgress}%`,
    opacity: uploadProgress ? 1 : 0,
  } as React.CSSProperties;

  if (willChildReturnNull(children)) {
    return null;
  }

  return (
    <div
      className={cx(`module ${moduleName}`, {
        preview: isCreateEditPreview,
        'module--processing': processing,
      })}
      data-qa-id={`${moduleName}-module`}
      id={`module-${moduleName}`}
      {...tracking}
      ref={moduleAnimation}
    >
      {showProgress && <div className="module__upload-progress" style={style} />}
      {cloneElement(children, {
        ...acyclicalProps,
        readonly,
        isCreationFlow: !window.location.pathname.startsWith('/event'),
      })}
    </div>
  );
};
